import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../App';
import axios from 'axios';
import { Star, Filter, Quote, Sparkles, Heart, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const STEP_LABELS = {
  onboarding: { fr: 'Onboarding', en: 'Onboarding', pt: 'Onboarding' },
  profile: { fr: 'Profil', en: 'Profile', pt: 'Perfil' },
  vision: { fr: 'Vision', en: 'Vision', pt: 'Visão' },
  content: { fr: 'Contenu', en: 'Content', pt: 'Conteúdo' },
  first_action: { fr: 'Première Action', en: 'First Action', pt: 'Primeira Ação' }
};

const EMOTION_EMOJIS = {
  aligned: '✨',
  inspired: '🔥',
  confused: '🤔',
  motivated: '💎'
};

const ExperienceCard = ({ experience, lang }) => {
  const stepLabel = STEP_LABELS[experience.step]?.[lang] || experience.step;
  const emotionEmoji = experience.emotion_tag ? EMOTION_EMOJIS[experience.emotion_tag] : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-[#1C1C1C] hover:border-[#D4AF37]/30 transition-all p-6 relative"
    >
      {experience.featured && (
        <div className="absolute top-4 right-4">
          <Star className="text-[#D4AF37]" size={16} fill="#D4AF37" />
        </div>
      )}
      
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
          <span className="text-[#D4AF37] text-sm font-medium">
            {experience.user_name?.charAt(0)?.toUpperCase() || '?'}
          </span>
        </div>
        <div>
          <p className="text-white text-sm font-medium">
            {experience.user_name || 'Anonyme'}
          </p>
          <p className="text-[#52525B] text-xs">
            Étape : {stepLabel}
          </p>
        </div>
      </div>
      
      {experience.feedback_text && (
        <div className="relative">
          <Quote className="absolute -top-1 -left-1 text-[#D4AF37]/20" size={20} />
          <p className="text-[#A1A1AA] text-sm leading-relaxed pl-4 italic">
            "{experience.feedback_text}"
          </p>
        </div>
      )}
      
      {emotionEmoji && (
        <div className="mt-4 flex items-center gap-2">
          <span className="text-lg">{emotionEmoji}</span>
          <span className="text-[#71717A] text-xs capitalize">
            {experience.emotion_tag}
          </span>
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-[#1C1C1C]">
        <p className="text-[#52525B] text-xs">
          {new Date(experience.created_at).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </p>
      </div>
    </motion.div>
  );
};

const Experiences = () => {
  const [experiences, setExperiences] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';

  const fetchExperiences = async (step = null, reset = false) => {
    try {
      const skip = reset ? 0 : page * 20;
      const url = step 
        ? `${API}/experiences?step=${step}&skip=${skip}&limit=20`
        : `${API}/experiences?skip=${skip}&limit=20`;
      
      const response = await axios.get(url);
      
      if (reset) {
        setExperiences(response.data.experiences);
      } else {
        setExperiences(prev => [...prev, ...response.data.experiences]);
      }
      setHasMore(response.data.has_more);
    } catch (err) {
      console.error('Error fetching experiences:', err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/experiences/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchExperiences(null, true);
    fetchStats();
  }, []);

  const handleFilterChange = (step) => {
    setSelectedStep(step);
    setPage(0);
    setLoading(true);
    fetchExperiences(step, true);
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchExperiences(selectedStep);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">
              EXPÉRIENCES
            </span>
          </div>
          
          <h1 
            className="text-white text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ce que vivent nos femmes
          </h1>
          
          <p className="text-[#71717A] max-w-xl">
            Des témoignages authentiques de femmes qui transforment leur vision en réalité.
          </p>
        </motion.div>

        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          >
            <div className="bg-[#0A0A0A] border border-[#1C1C1C] p-5 text-center">
              <div className="flex justify-center mb-2">
                <Users className="text-[#D4AF37]" size={20} />
              </div>
              <p className="text-white text-2xl font-serif">{stats.total}</p>
              <p className="text-[#52525B] text-xs">Témoignages</p>
            </div>
            
            {Object.entries(stats.by_emotion || {}).slice(0, 3).map(([emotion, count]) => (
              <div key={emotion} className="bg-[#0A0A0A] border border-[#1C1C1C] p-5 text-center">
                <div className="flex justify-center mb-2">
                  <span className="text-2xl">{EMOTION_EMOJIS[emotion]}</span>
                </div>
                <p className="text-white text-2xl font-serif">{count}</p>
                <p className="text-[#52525B] text-xs capitalize">{emotion}</p>
              </div>
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Filter className="text-[#52525B]" size={14} />
            <span className="text-[#52525B] text-xs">Filtrer par étape</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleFilterChange(null)}
              className={`px-4 py-2 text-sm transition-all ${
                !selectedStep
                  ? 'bg-[#D4AF37]/10 border border-[#D4AF37] text-[#D4AF37]'
                  : 'border border-[#27272A] text-[#71717A] hover:border-[#D4AF37]/50'
              }`}
              data-testid="filter-all"
            >
              Toutes
            </button>
            
            {Object.entries(STEP_LABELS).map(([step, labels]) => (
              <button
                key={step}
                onClick={() => handleFilterChange(step)}
                className={`px-4 py-2 text-sm transition-all ${
                  selectedStep === step
                    ? 'bg-[#D4AF37]/10 border border-[#D4AF37] text-[#D4AF37]'
                    : 'border border-[#27272A] text-[#71717A] hover:border-[#D4AF37]/50'
                }`}
                data-testid={`filter-${step}`}
              >
                {labels[lang]}
              </button>
            ))}
          </div>
        </motion.div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div 
                key={i} 
                className="bg-[#0A0A0A] border border-[#1C1C1C] p-6 h-48 animate-pulse"
              />
            ))}
          </div>
        ) : experiences.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-[#1C1C1C] rounded-full flex items-center justify-center">
              <Heart className="text-[#52525B]" size={24} />
            </div>
            <p className="text-[#71717A] mb-2">Aucune expérience pour le moment</p>
            <p className="text-[#52525B] text-sm">Les témoignages apparaîtront ici</p>
          </motion.div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experiences.map((exp, index) => (
                <ExperienceCard 
                  key={exp.checkpoint_id || index} 
                  experience={exp} 
                  lang={lang}
                />
              ))}
            </div>
            
            {hasMore && (
              <div className="text-center mt-10">
                <Button
                  onClick={loadMore}
                  variant="outline"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="load-more"
                >
                  Voir plus
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Experiences;
